import { useEffect, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';

import { fetchEntities } from '../../../services/syntiro_honey/data_ingestion.service';
import { EntityLookupItem } from '../../../types/syntiro_honey';

interface EntitySelectProps {
  /** Field label. */
  label: string;
  /** The record's current division id, or null. */
  divisionId: string | null;
  /** The record's current subdivision id, or null. */
  subdivisionId: string | null;
  /** The record's current site id, or null. */
  siteId: string | null;
  /** Display name of the current entity, for the fallback option while options load. */
  currentLabel?: string | null;
  /** Called with the chosen entity (its full org FK chain). */
  onSelect: (entity: EntityLookupItem) => void;
  /**
   * Fetch only this company's entities. The record's own company is the right scope
   * even before an entity resolves — an unattributed record would otherwise offer
   * every client's org tree, which is both wrong and unusable. Omit/null to fetch all.
   */
  companyId?: string | null;
  /**
   * True when the record is deliberately attached to the whole company, which is
   * what selects the COMPANY option. Without it a company-level record matches no
   * option and the field renders blank (Maxine ⑨).
   */
  companyWide?: boolean;
  /** Disable the control. */
  disabled?: boolean;
}

/** The id that identifies an entity at its deepest resolved level. */
function entityId(entity: EntityLookupItem): string {
  return entity.siteId ?? entity.subdivisionId ?? entity.divisionId ?? entity.companyId;
}

/** A human-readable org level for the option label. */
function entityLevel(entity: EntityLookupItem): string {
  switch (entity.kind) {
    case 'SITE':
      return 'Site';
    case 'SUBDIVISION':
      return 'Sub-division';
    case 'DIVISION':
      return 'Division';
    default:
      return 'Company';
  }
}

/** "Epping · Site" — the name + level the reviewer reads; the site code is internal. */
function optionLabel(entity: EntityLookupItem): string {
  return `${entity.name} · ${entityLevel(entity)}`;
}

/**
 * Entity dropdown backed by `GET /entities`, replacing the raw site-id text field so
 * a reviewer picks a site/division by NAME and never sees the opaque UUID (Maxine ①).
 * On selection it hands the form the full FK chain so the right dimension is PATCHed.
 *
 * The options are scoped to the record's company, so reassignment stays within the
 * org (DECISIONS #36): the server filters by `companyId` when the caller knows it,
 * and the resolved entity's company filters what is left. A record with neither sees
 * every entity. The list reloads when the company changes; a failed load leaves the
 * dropdown with just the current value.
 * @returns {JSX.Element}
 */
export default function EntitySelect({
  label,
  divisionId,
  subdivisionId,
  siteId,
  currentLabel,
  onSelect,
  companyId,
  companyWide,
  disabled
}: EntitySelectProps) {
  const [entities, setEntities] = useState<Array<EntityLookupItem>>([]);
  const [entitiesUnavailable, setEntitiesUnavailable] = useState<boolean>(false);

  useEffect(() => {
    let cancelled = false;
    setEntitiesUnavailable(false);
    fetchEntities(companyId ?? undefined)
      .then((items) => {
        if (!cancelled) {
          setEntities(items);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setEntitiesUnavailable(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  // The deepest currently-chosen id (site → subdivision → division), the dropdown's
  // value. A company-wide record has none of those, and its value IS the company —
  // which is exactly how the COMPANY option is keyed, so the two now meet.
  const currentId =
    siteId ?? subdivisionId ?? divisionId ?? (companyWide === true ? (companyId ?? '') : '');
  const current = entities.find((entity) => entityId(entity) === currentId);
  // Keep the dropdown within the record's own company; show all when unattributed.
  const scopeCompanyId = current?.companyId;
  const options =
    scopeCompanyId !== undefined
      ? entities.filter((entity) => entity.companyId === scopeCompanyId)
      : entities;

  const handleChange = (id: string): void => {
    const chosen = entities.find((entity) => entityId(entity) === id);
    if (chosen !== undefined) {
      onSelect(chosen);
    }
  };

  return (
    <TextField
      select
      label={label}
      size="small"
      fullWidth
      disabled={disabled || entitiesUnavailable}
      value={currentId}
      onChange={(event) => handleChange(event.target.value)}
      helperText={
        entitiesUnavailable
          ? 'Entity list unavailable — try again shortly'
          : 'Pick the site or division this record belongs to'
      }
    >
      {/* Fallback so the current value always matches an option, even before the
          list loads or if it is not in the (company-scoped) options. */}
      {current === undefined && currentId !== '' && (
        <MenuItem value={currentId}>{currentLabel ?? 'Current entity'}</MenuItem>
      )}
      {options.map((entity) => (
        <MenuItem key={entityId(entity)} value={entityId(entity)}>
          {optionLabel(entity)}
        </MenuItem>
      ))}
    </TextField>
  );
}
